import type { Editor } from '@tiptap/core';
import { DOMSerializer, type Node as ProseMirrorNode } from '@tiptap/pm/model';

import { formatCitationLabel } from '../../doc-engine/text';
import { nodeText } from '../resolvedProseMirrorText';
import { BLOCK_HEADER_ATTR, BLOCK_NODE_NAME } from '../../tiptap/nodes/block/constants';
import { INLINE_BLOCK_NODE_NAME } from '../../tiptap/nodes/inlineBlock/constants';
import { LITERATURE_CITATION_NODE_NAME } from '../../tiptap/nodes/literatureCitation/constants';
import { REFERENCE_NODE_NAME } from '../../tiptap/nodes/reference/constants';

/** A React node view mounts inside a `.react-renderer` container, so its own root is one level down. */
const nodeViewRootAt = (editor: Editor, pos: number): Element | null => {
  const dom = editor.view.nodeDOM(pos);
  if (!(dom instanceof Element)) return null;
  return dom.querySelector(':scope > [data-node-view-wrapper]') ?? dom;
};

const schemaText = (editor: Editor, node: ProseMirrorNode): string =>
  nodeText(DOMSerializer.fromSchema(editor.schema).serializeNode(node));

const blockBodyText = (root: Element): string =>
  [...root.children]
    .filter((child) => !child.hasAttribute(BLOCK_HEADER_ATTR))
    .map(nodeText)
    .filter(Boolean)
    .join('\n');

export const proseMirrorAtomText = (editor: Editor, node: ProseMirrorNode, pos: number): string => {
  if (node.type.name === LITERATURE_CITATION_NODE_NAME) {
    const index = typeof node.attrs.index === 'number' && Number.isFinite(node.attrs.index) ? node.attrs.index : null;
    return formatCitationLabel(index);
  }

  if (node.type.name === BLOCK_NODE_NAME) {
    const root = nodeViewRootAt(editor, pos);
    return root ? blockBodyText(root) : schemaText(editor, node);
  }

  if (node.type.name === INLINE_BLOCK_NODE_NAME || node.type.name === REFERENCE_NODE_NAME) {
    const root = nodeViewRootAt(editor, pos);
    return root ? nodeText(root) : schemaText(editor, node);
  }

  return schemaText(editor, node);
};
